import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import {
  parseBoolean,
  parseEnum,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { ConsequenceMatrixSelection } from './geckoRiskAssessmentConfig'
import { Severity } from './geckoSiteObservation'

/**
 * Lifecycle state of an observation. Absent on legacy entries; treat a missing
 * value as {@link ObservationStatus.OPEN} when reading.
 */
export enum ObservationStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  CLOSED = 'CLOSED',
}

export interface DeficiencyMarkupBounds {
  x_start: number
  y_start: number
  x_end: number
  y_end: number
  unit: string
}

function parseMarkupBounds(input: unknown): DeficiencyMarkupBounds | undefined {
  if (!input || typeof input !== 'object') return undefined
  const obj = input as Record<string, unknown>
  const x_start = typeof obj.x_start === 'number' ? obj.x_start : undefined
  const y_start = typeof obj.y_start === 'number' ? obj.y_start : undefined
  const x_end = typeof obj.x_end === 'number' ? obj.x_end : undefined
  const y_end = typeof obj.y_end === 'number' ? obj.y_end : undefined
  const unit = typeof obj.unit === 'string' ? obj.unit : undefined
  if (
    x_start === undefined ||
    y_start === undefined ||
    x_end === undefined ||
    y_end === undefined ||
    unit === undefined
  ) {
    return undefined
  }
  return { x_start, y_start, x_end, y_end, unit }
}

function parseMarkupBoundsList(
  input: unknown
): DeficiencyMarkupBounds[] | undefined {
  // TODO: remove fall back once we migrate all existing deficiencies to the new format
  const raw = Array.isArray(input) ? input : [input]
  const parsed = raw
    .map(parseMarkupBounds)
    .filter((b): b is DeficiencyMarkupBounds => b !== undefined)
  return parsed.length > 0 ? parsed : undefined
}

export interface DeficiencyPhoto {
  id: string
  filename: string
}

/**
 * Why a reviewer closed the observation by hand, as opposed to it being closed
 * because the condition was resolved and documented.
 *
 * Recorded on the observation itself rather than in a side record so the
 * reasoning travels with the thing it is about, and so the whole disposition is
 * one entry a reviewer already has permission to write.
 */
export interface DeficiencyClosure {
  justification: string
  closedAt: string
  /** Absent when the closing user could not be identified. */
  closedBy?: { id: string; name: string }
}

function parseClosure(input: unknown): DeficiencyClosure | undefined {
  if (!input || typeof input !== 'object') return undefined
  const candidate = input as Record<string, unknown>
  const justification = parseString(candidate.justification)
  const closedAt = parseString(candidate.closedAt)
  // A closure with no stated reason is not a closure — the reason is the point.
  if (!justification || !closedAt) return undefined

  const by = candidate.closedBy
  const byId = by && typeof by === 'object' ? parseString((by as Record<string, unknown>).id) : undefined
  const byName = by && typeof by === 'object' ? parseString((by as Record<string, unknown>).name) : undefined

  return {
    justification,
    closedAt,
    ...(byId && byName && { closedBy: { id: byId, name: byName } }),
  }
}

function parsePhotos(input: unknown): DeficiencyPhoto[] | undefined {
  if (!Array.isArray(input)) return undefined
  const photos = input.filter(
    (p): p is DeficiencyPhoto =>
      typeof p?.id === 'string' && typeof p?.filename === 'string'
  )
  return photos.length > 0 ? photos : undefined
}

export type GeckoComponentDeficiencyData = {
  title: string
  description?: string
  recommendations?: string
  severity?: Severity
  riskScore?: number
  likelihood?: number
  consequence?: number
  markupBounds?: DeficiencyMarkupBounds[]
  consequenceMatrixSelections?: ConsequenceMatrixSelection[]
  parentDeficiencyId?: string
  photos?: DeficiencyPhoto[]
  preferredPhotoId?: string
  /** Current lifecycle state. Absent => OPEN. */
  status?: ObservationStatus
  /**
   * Present only when a reviewer closed this by hand. Independent of `status`
   * being CLOSED, which also covers an observation closed because the condition
   * was documented as resolved.
   */
  closure?: DeficiencyClosure
  /**
   * True when the observation was authored by AI rather than a person. Permanent
   * provenance: nothing clears it, so it stays true even after a human reviews
   * or accepts the observation. Absent => false.
   */
  isAI?: boolean
}

export class GeckoComponentDeficiency extends GeckoEntryBase<
  GeckoComponentDeficiencyData,
  {
    organization: string
    site: string
    unit: string
    component?: string
    [RolodexEntryType.INSPECTION]?: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        title: parseString(json.data.title) ?? 'Untitled Deficiency',
        description: parseString(json.data.description),
        recommendations: parseString(json.data.recommendations),
        severity: parseString(json.data.severity) as Severity | undefined,
        riskScore: parseNumber(json.data.riskScore),
        likelihood: parseNumber(json.data.likelihood),
        consequence: parseNumber(json.data.consequence),
        markupBounds: parseMarkupBoundsList(json.data.markupBounds),
        consequenceMatrixSelections: json.data.consequenceMatrixSelections as
          | ConsequenceMatrixSelection[]
          | undefined,
        parentDeficiencyId: parseString(json.data.parentDeficiencyId),
        photos: parsePhotos(json.data.photos),
        preferredPhotoId: parseString(json.data.preferredPhotoId),
        status: parseEnum(ObservationStatus, json.data.status),
        isAI: parseBoolean(json.data.isAI),
        closure: parseClosure(json.data.closure),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component),
        [RolodexEntryType.INSPECTION]: parseString(
          json.tags[RolodexEntryType.INSPECTION]
        ),
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoComponentDeficiency(json)
  }
}
